import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';

import { isPresent } from 'services/operators';
import { DATE_FORMAT } from 'models/constants/input.constants';
import { ApplicationConfigService } from 'services/application-config.service';
import { createRequestOption } from 'app/shared/util/request-util';
import { getEdiHistoryIdentifier, IEdiHistory } from '../models/edi-history.model';
import { ApiProviderService } from './api-provider.service';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';

export type EntityResponseType = HttpResponse<IEdiHistory>;
export type EntityArrayResponseType = HttpResponse<IEdiHistory[]>;

@Injectable({ providedIn: 'root' })
export class EdiHistoryService {
  protected resourceUrl = API_ENDPOINTS.EDI_HISTORIES;
  protected downloadUrl = API_ENDPOINTS.EDI_DOWNLOAD_FILE;
  protected resourceForTOUrl = API_ENDPOINTS.EDI_HISTORIES_TRANSPORT_ORDER_ID;

  constructor(protected apiProvider: ApiProviderService, protected applicationConfigService: ApplicationConfigService) {}

  create(ediHistory: IEdiHistory): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(ediHistory);
    return this.apiProvider.post<IEdiHistory>(this.resourceUrl, copy).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(ediHistory: IEdiHistory): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(ediHistory);
    return this.apiProvider
      .put<IEdiHistory>(`${this.resourceUrl}/${getEdiHistoryIdentifier(ediHistory)}`, copy)
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(ediHistory: IEdiHistory): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(ediHistory);
    return this.apiProvider
      .patch<IEdiHistory>(`${this.resourceUrl}/${getEdiHistoryIdentifier(ediHistory)}`, copy)
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  findAllForTransportOrder(id: number): Observable<EntityArrayResponseType> {
    return this.apiProvider.get<IEdiHistory[]>(this.resourceForTOUrl(id)).pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.apiProvider.get<IEdiHistory>(`${this.resourceUrl}/${id}`, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  download(id: number, fileName: string): void {
    this.apiProvider.download<string>(this.downloadUrl(id), fileName);
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.apiProvider
      .get<IEdiHistory[]>(this.resourceUrl, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.apiProvider.delete(`${this.resourceUrl}/${id}`);
  }

  addEdiHistoryToCollectionIfMissing(ediHistoryCollection: IEdiHistory[], ...ediHistoriesToCheck: (IEdiHistory | null | undefined)[]): IEdiHistory[] {
    const ediHistories: IEdiHistory[] = ediHistoriesToCheck.filter(isPresent);
    if (ediHistories.length > 0) {
      const ediHistoryCollectionIdentifiers = ediHistoryCollection.map(ediHistoryItem => getEdiHistoryIdentifier(ediHistoryItem));
      const ediHistoriesToAdd = ediHistories.filter(ediHistoryItem => {
        const ediHistoryIdentifier = getEdiHistoryIdentifier(ediHistoryItem);
        if (ediHistoryIdentifier == null || ediHistoryCollectionIdentifiers.includes(ediHistoryIdentifier)) {
          return false;
        }
        ediHistoryCollectionIdentifiers.push(ediHistoryIdentifier);
        return true;
      });
      return [...ediHistoriesToAdd, ...ediHistoryCollection];
    }
    return ediHistoryCollection;
  }

  protected convertDateFromClient(ediHistory: IEdiHistory): IEdiHistory {
    return Object.assign({}, ediHistory, {
      date: ediHistory.date?.isValid() ? ediHistory.date.format(DATE_FORMAT) : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.date = res.body.date ? dayjs(res.body.date) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((ediHistory: IEdiHistory) => {
        ediHistory.date = ediHistory.date ? dayjs(ediHistory.date) : undefined;
      });
    }
    return res;
  }
}
